import { create } from 'zustand';
import type { PlayerCard } from '@machina/core';
import { trpc } from '../lib/trpc';
import { createSubscription } from '../lib/realtimeHelpers';

/**
 * Card Store
 *
 * Manages campaign-scoped player cards with Supabase realtime sync.
 * Follows the npcStore pattern.
 */

interface CardState {
  cards: PlayerCard[];
  isLoading: boolean;
  error: string | null;
  realtimeCleanup: (() => void) | null;

  // Actions
  loadCards: (campaignId: string) => Promise<void>;
  createCard: (campaignId: string, data: {
    title: string;
    frontText: string;
    backText?: string;
    cardType?: 'secret' | 'item' | 'clue' | 'rumor' | 'quest';
  }) => Promise<PlayerCard | null>;
  updateCard: (cardId: string, updates: Partial<Pick<PlayerCard, 'title' | 'frontText' | 'backText' | 'cardType'>>) => Promise<void>;
  dealCard: (cardId: string, recipientId: string) => Promise<void>;
  revealCard: (cardId: string) => Promise<void>;
  deleteCard: (cardId: string) => Promise<void>;

  // Realtime
  subscribeToRealtime: (campaignId: string) => void;
  unsubscribeFromRealtime: () => void;

  // Queries
  getMyCards: (userId: string) => PlayerCard[];
  getRevealedCards: () => PlayerCard[];
  getGMCards: () => PlayerCard[];
}

export const useCardStore = create<CardState>((set, get) => ({
  cards: [],
  isLoading: false,
  error: null,
  realtimeCleanup: null,

  // ==========================================================================
  // LOAD
  // ==========================================================================

  loadCards: async (campaignId: string) => {
    set({ isLoading: true, error: null });
    try {
      const cards = await trpc.cards.list.query({ campaignId });
      set({ cards, isLoading: false });
    } catch (err) {
      console.error('[CardStore] Error loading cards:', err);
      set({ error: 'Failed to load cards', isLoading: false });
    }
  },

  // ==========================================================================
  // MUTATIONS
  // ==========================================================================

  createCard: async (campaignId, data) => {
    try {
      const card = await trpc.cards.create.mutate({
        campaignId,
        title: data.title,
        frontText: data.frontText,
        backText: data.backText,
        cardType: data.cardType || 'secret',
      });

      set((state) => ({ cards: [card, ...state.cards] }));
      return card;
    } catch (err) {
      console.error('[CardStore] Error creating card:', err);
      set({ error: 'Failed to create card' });
      return null;
    }
  },

  updateCard: async (cardId, updates) => {
    try {
      const result = await trpc.cards.update.mutate({
        id: cardId,
        title: updates.title,
        frontText: updates.frontText,
        backText: updates.backText,
        cardType: updates.cardType,
      });

      set((state) => ({
        cards: state.cards.map((c) => (c.id === cardId ? result : c)),
      }));
    } catch (err) {
      console.error('[CardStore] Error updating card:', err);
      set({ error: 'Failed to update card' });
    }
  },

  dealCard: async (cardId, recipientId) => {
    try {
      const result = await trpc.cards.deal.mutate({ id: cardId, recipientId });

      set((state) => ({
        cards: state.cards.map((c) => (c.id === cardId ? result : c)),
      }));
    } catch (err) {
      console.error('[CardStore] Error dealing card:', err);
      set({ error: 'Failed to deal card' });
    }
  },

  revealCard: async (cardId) => {
    try {
      const result = await trpc.cards.reveal.mutate({ id: cardId });

      set((state) => ({
        cards: state.cards.map((c) => (c.id === cardId ? result : c)),
      }));
    } catch (err) {
      console.error('[CardStore] Error revealing card:', err);
      set({ error: 'Failed to reveal card' });
    }
  },

  deleteCard: async (cardId) => {
    try {
      await trpc.cards.delete.mutate({ id: cardId });

      set((state) => ({
        cards: state.cards.filter((c) => c.id !== cardId),
      }));
    } catch (err) {
      console.error('[CardStore] Error deleting card:', err);
      set({ error: 'Failed to delete card' });
    }
  },

  // ==========================================================================
  // REALTIME
  // ==========================================================================

  subscribeToRealtime: (campaignId: string) => {
    get().unsubscribeFromRealtime();

    const { cleanup } = createSubscription({
      channelName: `cards-${campaignId}`,
      table: 'machina_campaign_cards',
      filter: `campaign_id=eq.${campaignId}`,
      event: '*',
      onPayload: () => {
        get().loadCards(campaignId);
      },
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },

  // ==========================================================================
  // QUERIES
  // ==========================================================================

  getMyCards: (userId: string) => {
    return get().cards.filter(
      (c) => c.recipientId === userId && (c.visibility === 'private' || c.visibility === 'revealed')
    );
  },

  getRevealedCards: () => {
    return get().cards.filter((c) => c.visibility === 'revealed');
  },

  getGMCards: () => {
    return get().cards.filter((c) => c.visibility === 'gm_only');
  },
}));
